import { StatusId, CommandeMS } from '../types';

// Derivations PURES de l'onglet Candidature prestataire-MS. Aucune dependance React, aucune I/O.
// Gating 100% id-based (enum StatusId), jamais par libelle. La candidature presta = reponse
// technique + 3 criteres + offre financiere (prix par article), puis signature OTP.

// --- Vue-modele editable ------------------------------------------------------
export interface CritereReponses {
  organisation: string;
  moyens: string;
  social: string;
}

export interface LigneFinanciere {
  id: number | string;
  code?: string;
  nom?: string;
  prixHT: number;
}

export interface CandidatureView {
  reponseTechnique: string;
  criteres: CritereReponses;
  lignes: LigneFinanciere[];
}

// Erreurs de validation (inline, par champ). `prix` indexe par position de ligne.
export interface CandidatureErrors {
  reponseTechnique?: string;
  criteres: Partial<Record<keyof CritereReponses, string>>;
  prix: Record<number, boolean>;
}

const statusIdOf = (commande: CommandeMS): number | undefined =>
  commande.status?.id ?? commande.id_status;

// --- Expiration (id-based + date + chrono) ------------------------------------
const isDateExpired = (dateStr?: string | null): boolean => {
  if (!dateStr) return false;
  const target = new Date(dateStr);
  return !Number.isNaN(target.getTime()) && Date.now() >= target.getTime();
};

export const isCandidatureExpired = (commande: CommandeMS, chronoExpired = false): boolean =>
  chronoExpired ||
  statusIdOf(commande) === StatusId.Expirer ||
  isDateExpired(commande.expire_at) ||
  isDateExpired(commande.operation?.expire_at);

// --- Gating (id-based) --------------------------------------------------------
// Edition possible tant que le presta n'a pas candidate (statut < AAnalyser = 4) et non expire.
// En pratique cote presta : statut 3 (A candidater). Une fois candidate -> 5 (>= 4) = lecture seule.
// Fidele a l'oracle (Candidature.tsx:803,817,830,844 : disabled si status.id >= 4 || expire).
export const isCandidatureEditable = (commande: CommandeMS, chronoExpired = false): boolean => {
  if (isCandidatureExpired(commande, chronoExpired)) return false;
  const sid = statusIdOf(commande);
  return sid !== undefined && sid < StatusId.AAnalyser;
};

// Fenetre de consultation ouverte pour ce prestataire : statut "A candidater" (3) et delai non
// expire. C'est pendant cette fenetre qu'il peut echanger en Q&R et deposer son offre -> pilote les
// bandeaux d'avertissement des onglets Q&R / Consultation / Offre.
export const isConsultationEnCours = (commande: CommandeMS, chronoExpired = false): boolean =>
  !isCandidatureExpired(commande, chronoExpired) && statusIdOf(commande) === StatusId.ACandidater;

// Refus de la consultation : uniquement pendant la consultation
// (oracle Candidature.tsx:972 : bouton Refuser visible seulement si status.id === 3).
export const canRefuserConsultation = (commande: CommandeMS, chronoExpired = false): boolean =>
  isConsultationEnCours(commande, chronoExpired);

// Formate un horodatage ISO en "17/07/2026 à 18:22". null si absent ou invalide.
const formatDateHeure = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `${d.toLocaleDateString('fr-FR')} à ${heure}`;
};

// Date limite de remise des offres, formatee "17/07/2026 à 18:22". null si absente ou invalide.
export const dateLimiteRemiseOffres = (commande: CommandeMS): string | null =>
  formatDateHeure(commande.expire_at ?? commande.operation?.expire_at ?? null);

// On veut la PERSONNE, pas l'entite. Quelques preuves anciennes stockent "NOM (raison sociale...)" :
// on coupe la parenthese d'entite. Sans effet sur les noms propres, qui n'en contiennent pas.
const nomPersonne = (nom?: string | null): string | null => {
  const brut = nom?.trim();
  if (!brut) return null;
  const sansEntite = brut.split(' (')[0].trim();
  return sansEntite || null;
};

// Depot de l'offre : la PERSONNE qui a depose (pas l'entite) et l'horodatage, portes par la preuve
// de signature (core enrichit la commande avec `depot`). null tant que l'offre n'est pas deposee.
export const depotOffre = (commande: CommandeMS): { nom: string; dateHeure: string } | null => {
  const nom = nomPersonne(commande.depot?.nom);
  const dateHeure = formatDateHeure(commande.depot?.date);
  if (!nom || !dateHeure) return null;
  return { nom, dateHeure };
};

// Notification du marche subsequent : la signature de l'ACHETEUR vaut notification au prestataire.
// Disponible une fois le marche signe des deux cotes (statut Valider = 11) — c'est exactement ce qui
// ouvre les onglets Messagerie et Evaluation (cf. commandeMsTabs). Le numero definitif du marche vit
// sur `operation.numero` (reecrit en "MS-25-..." par validerOperation cote subsequent).
export const notificationMarche = (
  commande: CommandeMS,
): { numero: string; nom: string; dateHeure: string } | null => {
  if (statusIdOf(commande) !== StatusId.Valider) return null;
  const numero = commande.operation?.numero;
  const nom = nomPersonne(commande.notification?.nom);
  const dateHeure = formatDateHeure(commande.notification?.date);
  if (!numero || !nom || !dateHeure) return null;
  return { numero: String(numero), nom, dateHeure };
};

// --- Derivation de la vue depuis la commande chargee --------------------------
// Les criteres viennent de note_technique (description_operationnel/humain/environnement), la
// reponse technique de `reponse`, les prix de articles[].pivot.prix (cf. oracle Candidature.tsx
// :228-256, qui re-fetchait /subsequents/commande/{id} ; ici le parent l'a deja charge -> pas d'I/O).
export const deriveCandidatureView = (commande: CommandeMS): CandidatureView => {
  const nt = commande.note_technique ?? {};
  return {
    reponseTechnique: commande.reponse ?? '',
    criteres: {
      organisation: nt.description_operationnel ?? '',
      moyens: nt.description_humain ?? '',
      social: nt.description_environnement ?? '',
    },
    lignes: (commande.articles ?? []).map((a) => ({
      id: a.id ?? '',
      code: a.code,
      nom: a.nom,
      prixHT: Number(a.pivot?.prix) || 0,
    })),
  };
};

// Total HT = somme des prix bruts (nombres, pas la chaine affichee). Display-only au rendu.
export const totalHT = (lignes: LigneFinanciere[]): number =>
  lignes.reduce((sum, l) => sum + (Number(l.prixHT) || 0), 0);

// --- Validation ---------------------------------------------------------------
// Reponse technique + 3 criteres requis (non vides) ; chaque prix HT > 0 (oracle
// validateAllFields l.305-327 + validatePrixHT l.356-371).
export const validateCandidature = (
  view: CandidatureView,
): { valid: boolean; errors: CandidatureErrors } => {
  const errors: CandidatureErrors = { criteres: {}, prix: {} };
  let valid = true;

  if (!view.reponseTechnique.trim()) {
    errors.reponseTechnique = 'Ce champ est requis';
    valid = false;
  }
  (['organisation', 'moyens', 'social'] as const).forEach((c) => {
    if (!view.criteres[c].trim()) {
      errors.criteres[c] = 'Ce champ est requis';
      valid = false;
    }
  });
  view.lignes.forEach((l, i) => {
    if (l.prixHT === undefined || l.prixHT === null || l.prixHT <= 0) {
      errors.prix[i] = true;
      valid = false;
    }
  });

  return { valid, errors };
};

// --- Payload candidater / brouillon -------------------------------------------
// Forme attendue par /subsequents/{id}/candidater et /save (oracle l.404-413, 493-502) :
// criteres { organisation:{reponse}, ... }, articles [{ id, prixHT }].
// NB departementId/regionId : le microservice subsequent (CommandeController::saveCandidater) ne LIT
// ni l'un ni l'autre -> champs inertes. On derive departementId de la commande par commodite ;
// regionId reste undefined (non disponible cote commande). L'oracle, lui, les envoyait tous deux
// undefined (apiData n'etait jamais passe a Candidature) : divergence de valeur sans effet backend.
export interface CandidaterPayload {
  reponseTechnique: string;
  criteres: Record<'organisation' | 'moyens' | 'social', { reponse: string }>;
  articles: Array<{ id: number | string; prixHT: number }>;
  departementId?: number | string;
  regionId?: number | string;
}

export const buildCandidaterPayload = (
  view: CandidatureView,
  commande: CommandeMS,
): CandidaterPayload => ({
  reponseTechnique: view.reponseTechnique,
  criteres: {
    organisation: { reponse: view.criteres.organisation },
    moyens: { reponse: view.criteres.moyens },
    social: { reponse: view.criteres.social },
  },
  articles: view.lignes.map((l) => ({ id: l.id, prixHT: l.prixHT })),
  departementId: commande.operation?.lot_departement?.departement?.id,
  regionId: undefined,
});

// --- formData (viewMode) pour FormulaireAdministratif -------------------------
// FormulaireAdministratif attend une prop formData typee (champs requis). On la construit depuis
// commande.operation (calque l'enrichissement apiData de l'oracle l.55-89), au lieu de passer
// commande.formulaire||{} (qui serait quasi vide cote MS et non type).
export interface AdminFormData {
  nomOperation: string;
  referenceInterne?: string;
  adresse: string;
  codePostal: string;
  ville: string;
  coordonneesMaitrise?: string;
  coordonneesMaitriseDOuvrage?: string;
  delaisConsultation: number;
  delaisFinOperation?: number;
  visitePreavis: boolean;
  conditionVisite?: string;
  besoin: string;
  dateDebutOperation?: string;
  dateFinOperation?: string;
  operation: { visite: string | boolean; conditionVisite: string };
}

const diffDays = (debut?: string, fin?: string): number | null => {
  if (!debut || !fin) return null;
  const d = new Date(debut);
  const f = new Date(fin);
  if (Number.isNaN(d.getTime()) || Number.isNaN(f.getTime())) return null;
  return Math.ceil(Math.abs(f.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
};

export const buildAdminFormData = (commande: CommandeMS): AdminFormData => {
  const op = commande.operation ?? {};
  const aVisite = op.visite === true || op.visite === 'Oui' || op.visite === 1 || op.visite === '1';

  let delaisFinOperation = 30;
  const fromDates = diffDays(op.date_debut_operation, op.date_fin_operation);
  if (fromDates !== null) delaisFinOperation = fromDates;

  return {
    nomOperation: op.nom ?? 'Pas précisé',
    referenceInterne: op.reference,
    adresse: op.adresse ?? 'Pas précisé',
    codePostal: op.code_postal ?? '',
    ville: op.ville ?? '',
    coordonneesMaitrise: op.coordonnees_maitrise,
    coordonneesMaitriseDOuvrage: op.coordonnees_maitrise_ouvrage,
    delaisConsultation: 10,
    delaisFinOperation,
    visitePreavis: aVisite,
    conditionVisite: op.condition_visite,
    besoin: op.besoin ?? 'Pas précisé',
    dateDebutOperation: op.date_debut_operation,
    dateFinOperation: op.date_fin_operation,
    operation: {
      visite: aVisite ? 'Oui' : 'Non',
      conditionVisite: aVisite ? op.condition_visite ?? 'Pas précisé' : '',
    },
  };
};
